import { useState, useCallback } from 'react';
import { apiClient } from '../services/apiClient';
import type { Matter, MatterWithClient, UpdateMatterRequest } from '../types';

interface UseMatterReturn {
  matter: Matter | null;
  isLoading: boolean;
  error: string | null;
  loadMatter: (matterId: string) => Promise<void>;
  updateMatter: (matterId: string, data: UpdateMatterRequest) => Promise<Matter>;
  clearError: () => void;
}

interface UseMatterWithClientReturn {
  matter: MatterWithClient | null;
  isLoading: boolean;
  error: string | null;
  loadMatter: (matterId: string) => Promise<void>;
  updateMatter: (matterId: string, data: UpdateMatterRequest) => Promise<Matter>;
  clearError: () => void;
}

export const useMatter = (): UseMatterReturn => {
  const [matter, setMatter] = useState<Matter | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const loadMatter = useCallback(async (matterId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const fetchedMatter = await apiClient.getMatter(matterId);
      setMatter(fetchedMatter);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load matter';
      setError(errorMessage);
      console.error('Load matter failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateMatter = useCallback(async (matterId: string, data: UpdateMatterRequest): Promise<Matter> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Use empty string for clientId since backend uses matterId directly
      const updatedMatter = await apiClient.updateMatter('', matterId, data);
      setMatter(updatedMatter);
      return updatedMatter;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update matter';
      setError(errorMessage);
      console.error('Update matter failed:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    matter,
    isLoading,
    error,
    loadMatter,
    updateMatter,
    clearError
  };
};

export default useMatter;

export const useMatterWithClient = (): UseMatterWithClientReturn => {
  const [matter, setMatter] = useState<MatterWithClient | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const loadMatter = useCallback(async (matterId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const fetchedMatter = await apiClient.getMatterWithClient(matterId);
      setMatter(fetchedMatter);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load matter';
      setError(errorMessage);
      console.error('Load matter failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateMatter = useCallback(async (matterId: string, data: UpdateMatterRequest): Promise<Matter> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Use empty string for clientId since backend uses matterId directly
      const updatedMatter = await apiClient.updateMatter('', matterId, data);
      // Reload matter with client info after update
      await loadMatter(matterId);
      return updatedMatter;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update matter';
      setError(errorMessage);
      console.error('Update matter failed:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [loadMatter]);

  return {
    matter,
    isLoading,
    error,
    loadMatter,
    updateMatter,
    clearError
  };
};
